import { LazyIconWrapper } from "../ui/LazyIconWrapper";
import { createLazyIcon } from "../../lib/lazy-icons";

// Lazy load de toutes les icônes (X et Plus utilisées dans sidebar/éditeur conditionnels)
const X = createLazyIcon("X");
const Check = createLazyIcon("Check");
const ExternalLink = createLazyIcon("ExternalLink");

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useConversation } from "../conversation/ConversationProvider";
import {
  useEditorActions,
  useEditorState,
} from "../editor/EditorStateProvider";
import { useUIState } from "../prototype/UIStateProvider";
import { useMediaQuery } from "@core/hooks/useMediaQuery";
import GeminiChatInterface, {
  type GeminiChatHandle,
} from "../GeminiChatInterface";
import {
  FormSimulationIntegration,
  SimulationButton,
  SimulationModal,
  SimulationReport,
} from "../../exports/products";
import { WorkspaceEditor } from "./WorkspaceEditor";
import { MobileNavigationTabs as MobileNavTabs } from "../layout/MobileNavigationTabs";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { type Poll } from "../../lib/pollStorage";
import {
  getCurrentUserId,
  getFingerprint,
} from "../../lib/guestIdentityService";
import { usePoll } from "../../hooks/usePoll";
import { useWorkspaceNavigation } from "../../hooks/workspace/useWorkspaceNavigation";
import { useWorkspaceData } from "../../hooks/workspace/useWorkspaceData";
import { useWorkspaceDependencies } from "../../hooks/workspace/useWorkspaceDependencies";
import { WorkspaceModals } from "../modals/WorkspaceModals";
import { useToast } from "../../hooks/use-toast";
import { SafeguardSection } from "../polls/SafeguardSection";
import { QRCodeDisplay } from "../shared/QRCodeDisplay";

// Nouveaux imports pour le refactoring (ResponsiveLayout)
import { ResponsiveLayout } from "../layout/ResponsiveLayout";
import { ChatContainer } from "../layout/ChatContainer";
import { ProductHeader } from "../layout/products/ProductHeader";

/**
 * AI Creation Workspace - Espace de création avec IA
 *
 * Layout avec :
 * - Messages à gauche (chat IA)
 * - Créateur de sondage/formulaire à droite (par défaut)
 * - Input en bas
 */
interface AICreationWorkspaceProps {
  pollTypeFromUrl?: string;
  hideSidebar?: boolean;
  externalHeaderHeight?: number; // Hauteur du header externe en pixels (ex: 64 pour h-16)
}

export function AICreationWorkspace({
  pollTypeFromUrl: pollTypeFromProp,
  hideSidebar = false,
  externalHeaderHeight,
}: AICreationWorkspaceProps = {}) {
  const { t } = useTranslation(["creator", "common", "errors"]);
  const navigate = useNavigate();
  const location = useLocation();
  const chatRef = useRef<GeminiChatHandle>(null);
  const { toast } = useToast();

  // Lire les paramètres de l'URL pour forcer le remontage du chat
  const searchParams = new URLSearchParams(location.search);
  const resumeId = searchParams.get("resume");
  const conversationId = searchParams.get("conversationId");
  const editPollId = searchParams.get("edit");
  const newChatTimestamp = searchParams.get("new");

  // Priorité : prop > pathname > query param > défaut "date"
  const pollTypeFromPathname = location.pathname.includes("/workspace/form")
    ? "form"
    : location.pathname.includes("/workspace/date")
      ? "date"
      : location.pathname.includes("/workspace/availability")
        ? "availability"
        : null;
  const pollTypeFromQuery = searchParams.get("type") as
    | "date"
    | "form"
    | "availability"
    | null;
  const pollTypeFromUrl =
    pollTypeFromProp || pollTypeFromPathname || pollTypeFromQuery || "date";

  // Convertir "availability" en "date" pour les composants qui ne supportent que "date" | "form"
  // MAIS GeminiChatInterface supporte maintenant "availability"
  const pollTypeForComponents: "date" | "form" | "availability" | "quizz" =
    pollTypeFromUrl === "availability"
      ? "availability"
      : pollTypeFromUrl === "form"
        ? "form"
        : pollTypeFromUrl === "quizz"
          ? "quizz"
          : "date";

  // Convertir "resume" en "conversationId" si nécessaire (Session 2 - Bug 3)
  useEffect(() => {
    if (resumeId && !conversationId) {
      const newUrl = `${location.pathname}?conversationId=${resumeId}`;
      navigate(newUrl, { replace: true });
    }
  }, [resumeId, conversationId, location.pathname, navigate]);

  const chatKey =
    resumeId || conversationId || editPollId || newChatTimestamp || "new-chat";

  // Get currentPoll FIRST before using it in other hooks
  const { isEditorOpen, currentPoll } = useEditorState();

  // Hook de données refactorisé (Story 2) - NOW currentPoll is available
  const { recentPolls, conversations } = useWorkspaceData({
    chatKey,
    currentPoll,
    conversationsRefreshKey: 0,
  });

  // Get media queries AFTER currentPoll is available
  const isTablet = useMediaQuery("(min-width: 769px) and (max-width: 1023px)");
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { openEditor, closeEditor, setCurrentPoll, createPollFromChat } =
    useEditorActions();

  // Si on est en mode édition et sur mobile, commencer par l'onglet editor
  const initialTab = editPollId && isMobile ? "editor" : "chat";

  console.log("AICreationWorkspace - Mode détection:", {
    editPollId,
    isMobile,
    initialTab,
    location: location.pathname + location.search,
  });

  // 🔥 NOUVEAU: Si on a editPollId mais pas conversationId, trouver la conversation associée
  useEffect(() => {
    if (editPollId && !conversationId && conversations.length > 0) {
      // Chercher la conversation associée à ce poll
      const associatedConversation = conversations.find(
        (conv) => conv.pollId === editPollId,
      );

      if (associatedConversation) {
        // Rediriger vers la même URL mais avec conversationId
        const newUrl = `${location.pathname}?edit=${editPollId}&conversationId=${associatedConversation.id}`;
        navigate(newUrl, { replace: true });
      }
    }
  }, [editPollId, conversationId, conversations, location.pathname, navigate]);

  // Hook de dépendances refactorisé (Story 2)
  const dependencies = useWorkspaceDependencies();
  const { user, voiceRecognition, modals } = dependencies;

  // 🔒 Guard création/édition : si ?edit=<id>, vérifier que le visiteur
  // courant est bien le créateur du sondage avant d'afficher l'éditeur.
  // Sans ça, n'importe qui peut forger l'URL pour ouvrir l'éditeur d'un
  // sondage tiers (la RLS côté serveur bloque l'UPDATE, mais l'UI doit
  // aussi refuser d'afficher le formulaire).
  //
  // Deux chemins d'identification :
  // - Créateur authentifié → poll.creator_id === user.id
  // - Créateur anonyme     → poll.creator_id === null et
  //                          poll.guest_fingerprint === fingerprint local
  const {
    data: editTargetPoll,
    isLoading: editTargetLoading,
    error: editTargetError,
  } = usePoll(editPollId || "", { enabled: !!editPollId });

  // Fingerprint navigateur (async) — nécessaire pour identifier le créateur
  // anonyme. Tant qu'il n'est pas résolu, on ne prend aucune décision de
  // redirect, sinon on redirige à tort avant même d'avoir pu comparer.
  const [currentFingerprint, setCurrentFingerprint] = useState<string | null>(
    null,
  );
  const [fingerprintLoaded, setFingerprintLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getFingerprint()
      .then((fp) => {
        if (cancelled) return;
        setCurrentFingerprint(fp);
        setFingerprintLoaded(true);
      })
      .catch(() => {
        // En cas d'erreur on débloque quand même : l'effet de guard décidera
        // sur la base d'un fingerprint null (échec → pas créateur).
        if (!cancelled) setFingerprintLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!editPollId) return;
    if (editTargetLoading) return;

    // Si le poll n'a pas pu être chargé (supprimé, introuvable, RLS SELECT refuse)
    if (editTargetError || !editTargetPoll) {
      toast({
        title: t("errors:poll_not_found_title"),
        description: t("errors:poll_not_found_description"),
        variant: "destructive",
      });
      navigate("/dashboard", { replace: true });
      return;
    }

    const pollCreatorId = editTargetPoll.creator_id;
    const pollGuestFingerprint = (
      editTargetPoll as { guest_fingerprint?: string | null }
    ).guest_fingerprint;

    let isCreator = false;

    if (pollCreatorId) {
      // Poll créé par un utilisateur authentifié : match sur l'UUID Supabase.
      isCreator = !!user?.id && pollCreatorId === user.id;
    } else if (pollGuestFingerprint) {
      // Poll créé en anonyme : on attend d'avoir résolu le fingerprint local
      // avant de trancher, pour ne pas rediriger le vrai créateur à tort.
      if (!fingerprintLoaded) return;
      isCreator =
        !!currentFingerprint && pollGuestFingerprint === currentFingerprint;
    }

    if (!isCreator) {
      toast({
        title: t("errors:access_denied_title"),
        description: t("errors:poll_edit_creator_only"),
        variant: "destructive",
      });
      navigate(`/poll/${editTargetPoll.slug || editPollId}/results`, {
        replace: true,
      });
      return;
    }

    // Hydrate editor with the poll data
    setCurrentPoll(editTargetPoll as Poll);
    if (!isMobile) {
      openEditor();
    }
  }, [
    editPollId,
    editTargetPoll,
    editTargetLoading,
    editTargetError,
    user?.id,
    currentFingerprint,
    fingerprintLoaded,
    navigate,
    toast,
    t,
    setCurrentPoll,
    openEditor,
    isMobile,
  ]);

  // Mapping des états de modale pour minimiser les changements JSX
  const authModalOpen = modals.auth.open;
  const setAuthModalOpen = modals.auth.setOpen;
  const signOutDialogOpen = modals.signOut.open;
  const setSignOutDialogOpen = modals.signOut.setOpen;
  const betaKeyModalOpen = modals.betaKey.open;
  const setBetaKeyModalOpen = modals.betaKey.setOpen;

  const {
    mobileActiveTab,
    setMobileActiveTab,
    showManualEditorOnMobile,
    setShowManualEditorOnMobile,
    showBothSides,
  } = useWorkspaceNavigation({
    initialTab: initialTab,
    pollType: pollTypeForComponents,
    isTablet,
  });

  // Forcer l'onglet éditeur si on est en mode édition sur mobile
  useEffect(() => {
    if (editPollId && isMobile && mobileActiveTab !== "editor") {
      console.log("AICreationWorkspace - Forcing editor tab for edit mode");
      setMobileActiveTab("editor");
      setShowManualEditorOnMobile(true);
    }
  }, [
    editPollId,
    isMobile,
    mobileActiveTab,
    setMobileActiveTab,
    setShowManualEditorOnMobile,
  ]);

  const [publishedPoll, setPublishedPoll] = useState<Poll | null>(null);

  // 🔥 FIX: Reset the success screen when a new conversation/reset is triggered
  useEffect(() => {
    const handleChatReset = () => {
      setPublishedPoll(null);
    };

    window.addEventListener("chat-reset", handleChatReset);
    return () => {
      window.removeEventListener("chat-reset", handleChatReset);
    };
  }, []);

  // 📜 DOCTRINE PRICING : la création de sondages est illimitée pour tous les plans.
  //    Il n'existe donc aucun "pollQuotaExceeded" à écouter. Les plafonds IA
  //    (0/10/∞ selon le plan) sont gérés dans useAiMessageQuota + QuotaLimitModal.

  const { isMobile: isMobileFromContext, setIsSidebarOpen } = useUIState();
  const isMobileFromMediaQuery = useMediaQuery("(max-width: 768px)");
  // Utiliser la détection directe si le contexte n'est pas disponible
  const isMobileEffective = isMobileFromContext || isMobileFromMediaQuery;

  // Fermer le preview et nettoyer le poll quand on démarre un nouveau chat (Session 2 - Bug 4)
  useEffect(() => {
    if (newChatTimestamp && !conversationId && !resumeId && !editPollId) {
      closeEditor();
      setCurrentPoll(null);
    }
  }, [
    newChatTimestamp,
    conversationId,
    resumeId,
    editPollId,
    closeEditor,
    setCurrentPoll,
  ]);

  // 🔥 NOUVEAU: Créer un sondage par défaut quand on arrive depuis la landing page sans conversation
  useEffect(() => {
    const isFromLanding =
      location.pathname.includes("/workspace/") &&
      (location.pathname.includes("/date") ||
        location.pathname.includes("/form") ||
        location.pathname.includes("/availability"));

    if (
      isFromLanding &&
      !conversationId &&
      !resumeId &&
      !editPollId &&
      !currentPoll &&
      !newChatTimestamp
    ) {
      const creatorId = user?.id || getCurrentUserId();
      const defaultPoll =
        pollTypeForComponents === "form"
          ? {
              id: `default-form-${Date.now()}`,
              creator_id: creatorId,
              title: t("creator:default_form_title"),
              slug: `${t("creator:default_form_slug")}-${Date.now()}`,
              status: "draft" as const,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              type: "form" as const,
              questions: [
                {
                  id: "q1",
                  kind: "text" as const,
                  title: t("creator:default_question_title"),
                  type: "text" as const,
                  required: true,
                },
              ],
            }
          : pollTypeForComponents === "availability"
            ? {
                id: `default-availability-${Date.now()}`,
                creator_id: creatorId,
                title: t("creator:default_availability_title"),
                slug: `${t("creator:default_availability_slug")}-${Date.now()}`,
                status: "draft" as const,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                type: "availability" as const,
              }
            : {
                id: `default-date-${Date.now()}`,
                creator_id: creatorId,
                title: t("creator:default_date_title"),
                slug: `${t("creator:default_date_slug")}-${Date.now()}`,
                status: "draft" as const,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                type: "date" as const,
                settings: {
                  showTimeSlots: true,
                  selectedDates: [
                    new Date().toISOString().split("T")[0],
                    new Date(Date.now() + 86400000).toISOString().split("T")[0],
                  ],
                  showLogo: true,
                },
              };

      setCurrentPoll(defaultPoll);

      if (!isMobile) {
        setTimeout(() => {
          openEditor();
        }, 100);
      }
    }
  }, [
    location.pathname,
    conversationId,
    resumeId,
    editPollId,
    currentPoll,
    newChatTimestamp,
    pollTypeForComponents,
    setCurrentPoll,
    isMobile,
    openEditor,
    user,
    t,
  ]);

  const conversationContext = useConversation();
  const clearConversation = useMemo(
    () => conversationContext?.clearConversation || (() => {}),
    [conversationContext?.clearConversation],
  );

  useEffect(() => {
    if (newChatTimestamp) {
      clearConversation();
      const currentPath = location.pathname;
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete("new");
      const newUrl = searchParams.toString()
        ? `${currentPath}?${searchParams.toString()}`
        : currentPath;
      navigate(newUrl, { replace: true });

      setTimeout(() => {
        openEditor();
      }, 100);
    }
  }, [
    newChatTimestamp,
    clearConversation,
    openEditor,
    navigate,
    location.pathname,
    location.search,
  ]);

  if (publishedPoll) {
    const paddingTop = externalHeaderHeight
      ? `${externalHeaderHeight / 4}rem`
      : "pt-20";

    // Audience info attachée par PollCreator pour personnaliser le succès.
    const audience = (publishedPoll as any).__audience as
      | {
          mode: "group" | "emails" | "link";
          sentCount: number;
          groupName?: string;
          emailDelivered: boolean;
        }
      | undefined;

    const baseDescriptionByType =
      publishedPoll.type === "date"
        ? t("creator:success_base_description_date", {
            title: publishedPoll.title,
          })
        : publishedPoll.type === "form"
          ? t("creator:success_base_description_form", {
              title: publishedPoll.title,
            })
          : t("creator:success_base_description_availability", {
              title: publishedPoll.title,
            });

    let audienceTitle: string | undefined;
    let audienceDescription: string | undefined;
    if (audience) {
      if (audience.mode === "group" && audience.emailDelivered) {
        audienceTitle = t("creator:success_audience_group_title", {
          groupName: audience.groupName ?? t("creator:group_unnamed"),
        });
        audienceDescription = t("creator:success_audience_group_description", {
          count: audience.sentCount,
          groupName: audience.groupName ?? t("creator:group_unnamed"),
        });
      } else if (audience.mode === "emails" && audience.emailDelivered) {
        audienceTitle = t("creator:success_audience_emails_title");
        audienceDescription = t("creator:success_audience_emails_description", {
          count: audience.sentCount,
        });
      } else if (audience.mode === "link") {
        audienceTitle = t("creator:success_audience_link_title");
        audienceDescription = t("creator:success_audience_link_description");
      } else if (!audience.emailDelivered) {
        audienceDescription = t(
          "creator:success_audience_email_failed_description",
        );
      }
    }

    const themeColors =
      publishedPoll.type === "date"
        ? {
            bgGradient: "from-blue-900/30 to-blue-800/20",
            borderColor: "border-blue-700/30",
            iconBg: "bg-blue-500/20",
            iconColor: "text-blue-500",
            title: audienceTitle ?? t("creator:success_title_date"),
            description: audienceDescription ?? baseDescriptionByType,
          }
        : publishedPoll.type === "form"
          ? {
              bgGradient: "from-violet-900/30 to-violet-800/20",
              borderColor: "border-violet-700/30",
              iconBg: "bg-violet-500/20",
              iconColor: "text-violet-500",
              title: audienceTitle ?? t("creator:success_title_form"),
              description: audienceDescription ?? baseDescriptionByType,
            }
          : {
              bgGradient: "from-green-900/30 to-green-800/20",
              borderColor: "border-green-700/30",
              iconBg: "bg-green-500/20",
              iconColor: "text-green-500",
              title: audienceTitle ?? t("creator:success_title_availability"),
              description: audienceDescription ?? baseDescriptionByType,
            };

    return (
      <div className="min-h-screen-safe bg-[#0a0a0a] w-full flex items-center justify-center">
        <div className={`${paddingTop} w-full`}>
          <div className="max-w-2xl mx-auto p-4 sm:p-6">
            <div
              className={`bg-gradient-to-br ${themeColors.bgGradient} rounded-lg ${themeColors.borderColor} p-8 text-center space-y-6`}
            >
              <div className="flex justify-center">
                <div
                  className={`w-16 h-16 ${themeColors.iconBg} rounded-full flex items-center justify-center`}
                >
                  <Check className={`w-10 h-10 ${themeColors.iconColor}`} />
                </div>
              </div>

              <div>
                <h1
                  className="text-3xl font-bold text-white mb-2"
                  data-testid="success-message"
                >
                  {themeColors.title}
                </h1>
                <p className="text-gray-300">{themeColors.description}</p>
              </div>

              {!user && (
                <div className="mb-6 max-w-md mx-auto">
                  <SafeguardSection />
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
                <Link
                  to="/dashboard"
                  className={`inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r ${
                    publishedPoll.type === "date"
                      ? "from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
                      : publishedPoll.type === "form"
                        ? "from-violet-500 to-violet-600 hover:from-violet-600 hover:to-violet-700"
                        : "from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
                  } text-white rounded-lg font-semibold transition-all shadow-lg`}
                  data-testid="go-to-dashboard-button"
                >
                  <Check className="w-5 h-5" />
                  {t("creator:go_to_dashboard")}
                </Link>
                <Link
                  to={`/poll/${publishedPoll.slug || publishedPoll.id}/vote`}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-transparent text-gray-300 border-2 border-gray-300 rounded-lg font-semibold hover:bg-gray-800 transition-colors"
                  data-testid="view-poll-button"
                >
                  <ExternalLink className="w-5 h-5" />
                  {publishedPoll.type === "form"
                    ? t("creator:view_form")
                    : t("creator:view_poll")}
                </Link>
              </div>

              <div className="pt-4 border-t border-gray-600">
                <p className="text-sm text-gray-400 mb-3">
                  {t("creator:share_link_label")}
                </p>
                <div className="flex flex-col sm:flex-row gap-2 items-center justify-center">
                  <code
                    className="px-4 py-2 bg-[#1e1e1e] border border-gray-700 rounded text-sm font-mono text-gray-300 break-all"
                    data-testid="share-link"
                  >
                    {window.location.origin}/poll/
                    {publishedPoll.slug || publishedPoll.id}/vote
                  </code>
                  <button
                    onClick={() => {
                      if (isMobile) setIsSidebarOpen(false);
                      const url = `${window.location.origin}/poll/${publishedPoll.slug || publishedPoll.id}/vote`;
                      navigator.clipboard.writeText(url);
                      toast({
                        title: t("creator:link_copied_title"),
                        description: t("creator:link_copied_description"),
                      });
                    }}
                    className={`px-4 py-3 ${
                      publishedPoll.type === "date"
                        ? "bg-blue-600 hover:bg-blue-700"
                        : publishedPoll.type === "form"
                          ? "bg-violet-600 hover:bg-violet-700"
                          : "bg-green-600 hover:bg-green-700"
                    } text-white rounded-lg transition-colors text-sm font-medium whitespace-nowrap`}
                    data-testid="copy-link-button"
                  >
                    {t("common:copy")}
                  </button>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-600 flex justify-center">
                <QRCodeDisplay
                  url={`${window.location.origin}/poll/${publishedPoll.slug || publishedPoll.id}/vote`}
                  size={200}
                  label={t("creator:qr_scan_label")}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <ResponsiveLayout
        header={
          <ProductHeader
            productType={pollTypeForComponents}
            showAuth={true}
            showAuthButtons={!user}
          />
        }
        activeTab={mobileActiveTab === "chat" ? "left" : "right"}
        onTabChange={(tab) => {
          const newTab = tab === "left" ? "chat" : "editor";
          console.log(
            "AICreationWorkspace - Changement onglet (ResponsiveLayout):",
            {
              currentTab: mobileActiveTab,
              clickedTab: tab,
              newTab,
              isMobile,
              editPollId,
            },
          );
          setMobileActiveTab(newTab);
          if (newTab === "chat") {
            setShowManualEditorOnMobile(false);
          } else {
            setShowManualEditorOnMobile(true);
          }
        }}
        showBothSides={showBothSides}
        leftContent={
          <ChatContainer>
            <GeminiChatInterface
              ref={chatRef}
              key={chatKey}
              pollType={pollTypeForComponents}
              onPollCreated={async (pollData) => {
                await createPollFromChat(pollData as any);
                if (isMobile) {
                  setMobileActiveTab("editor");
                  setShowManualEditorOnMobile(false);
                }
              }}
              onUserMessage={() => {}}
              hideStatusBar={true}
              darkTheme={true}
              voiceRecognition={voiceRecognition}
            />
          </ChatContainer>
        }
        rightContent={
          <WorkspaceEditor
            isMobile={isMobile}
            showManualEditorOnMobile={showManualEditorOnMobile}
            setShowManualEditorOnMobile={setShowManualEditorOnMobile}
            isEditorOpen={isEditorOpen}
            closeEditor={closeEditor}
            currentPoll={currentPoll}
            pollTypeFromUrl={pollTypeFromUrl}
            setMobileActiveTab={setMobileActiveTab}
            setPublishedPoll={setPublishedPoll}
          />
        }
        mobileNavButtons={
          pollTypeForComponents !== "availability" &&
          pollTypeForComponents !== "quizz" ? (
            <MobileNavTabs
              activeTab={mobileActiveTab}
              onTabChange={(tab: any) => {
                const newTab = tab; // MobileNavTabs utilise directement 'chat' ou 'editor'
                console.log(
                  "AICreationWorkspace - Changement onglet (MobileNavTabs):",
                  {
                    currentTab: mobileActiveTab,
                    clickedTab: tab,
                    newTab,
                    isMobile,
                    editPollId,
                  },
                );
                setMobileActiveTab(newTab);
                if (newTab === "chat") {
                  setShowManualEditorOnMobile(false);
                } else if (newTab === "editor") {
                  setShowManualEditorOnMobile(true);
                }
              }}
              pollType={pollTypeForComponents}
              hasPoll={!!publishedPoll}
            />
          ) : undefined
        }
      />

      <WorkspaceModals dependencies={dependencies} />
    </>
  );
}
